// ============================================================
// Single Product View
// Shows N products per page (page_length=N, start=M).
// N is chosen by the shop user via the "Pro Seite" selector
// (persisted in localStorage, default 1 = one product at a time,
// PIT-TASK-2027-000138).
// Prev/Next trigger a new API request — fast, no bulk loading.
// ============================================================

webshop.ProductSingle = class {
	/*
	 * Options:
	 *   query_args       – filters object from get_query_filters()
	 *   settings         – Webshop Settings doc
	 *   products_section – jQuery wrapper for #products-single-area
	 *   preference       – current view preference string
	 */
	constructor(options) {
		Object.assign(this, options);

		if (this.preference !== "Single View") {
			this.products_section.addClass("hidden");
			return;
		}

		this.current_index = 0;  // 0-based offset
		this.total_count   = 0;
		this.products_section.empty();
		this._fetch(0);
	}

	// ── API ──────────────────────────────────────────────────────

	_per_page_options() {
		// Per customer feedback (PIT-TASK-2027-000138): only 1, 2 and 4.
		// 2 = two half-width cards side by side, 4 = 2x2 grid
		// (quarter-sized cards, like the grid view).
		return [1, 2, 4];
	}

	_get_per_page() {
		// Products per page in the single view.
		// 1. User choice from the "Pro Seite" selector (localStorage).
		// 2. Fallback 1.
		const stored = cint(localStorage.getItem("ws_single_per_page"));
		if (stored && this._per_page_options().includes(stored)) return stored;
		return 1;
	}

	_fetch(start) {
		const me = this;
		this._set_loading(true);

		const per_page = this._get_per_page();
		this._per_page = per_page;

		// Build clean args — always override start, page_length, and from_filters.
		// from_filters must be false so the API never resets start back to 0.
		const args = Object.assign({}, this.query_args, {
			page_length: per_page,
			start: start,
			from_filters: false,
		});

		frappe.call({
			method: "webshop.webshop.api.get_product_filter_data",
			args: { query_args: args },
			callback(result) {
				me._set_loading(false);
				if (!result || result.exc || !result.message || result.message.exc) return;

				const msg = result.message;
				if (!msg.items || !msg.items.length) return;

				me.total_count = msg.items_count || 1;
				me.settings    = msg.settings;
				me.current_index = start;

				me._render_items(msg.items);
			}
		});
	}

	// ── Shell (built once) ───────────────────────────────────────

	_ensure_shell() {
		if (this.products_section.find(".ws-single-view").length) return;

		this.products_section.append(`
			<div class="ws-single-toolbar">
				<label class="ws-single-perpage-label" for="ws-single-perpage">
					${ __("Pro Seite") }:
				</label>
				<select id="ws-single-perpage" class="ws-single-perpage form-control"></select>
			</div>
			<div class="ws-single-view">
				<button class="ws-single-nav ws-single-prev" disabled>
					<svg class="icon icon-lg"><use href="#icon-left"></use></svg>
				</button>
				<div class="ws-single-card-wrap"></div>
				<button class="ws-single-nav ws-single-next">
					<svg class="icon icon-lg"><use href="#icon-right"></use></svg>
				</button>
			</div>
		`);

		this._bind_nav();
		this._bind_qty_events();
	}

	_set_loading(on) {
	        if (on) {
	            this._ensure_shell();
	            // PIT-TASK-2027-000235: simple loading spinner + short text
	            // instead of the old card skeleton placeholder.
	            this.products_section.find(".ws-single-card-wrap").html(`
	                <div class="ws-listing-loading ws-single-loading">
	                    <div class="ws-loading-spinner"></div>
	                    <div class="ws-loading-text">${ __("Produkte werden geladen…") }</div>
	                </div>
	            `);
	            // Disable nav while loading
	            this.products_section.find(".ws-single-nav").prop("disabled", true);
	        }
	    }

	// ── Card rendering ───────────────────────────────────────────

	_render_items(items) {
		this._ensure_shell();
		this._render_perpage_select();

		const per_page = this._per_page || 1;
		const $wrap = this.products_section.find(".ws-single-card-wrap");
		$wrap.toggleClass("ws-multi", items.length > 1)
			// Adaptive card sizing (PIT-TASK-2027-000138):
			// 1 per page -> slightly smaller showcase card,
			// 2 per page -> half-width cards side by side,
			// 4 per page -> 2x2 grid (quarter-sized cards).
			.toggleClass("ws-cols-1", per_page === 1)
			.toggleClass("ws-cols-2", per_page === 2)
			.toggleClass("ws-cols-4", per_page === 4)
			.html(items.map(item => {
				const title = (item.web_item_name || item.item_name || item.item_code || "")
					.substr(0, 200);
				return this._get_card_html(item, title);
			}).join(""));

		// Portrait image zoom (PIT-TASK-2027-000278): single view cards
		// re-render on every prev/next page fetch.
		webshop.apply_portrait_image_zoom($wrap);

		// Nav state — page steps by _per_page
		const i = this.current_index;
		const n = this.total_count;
		this.products_section.find(".ws-single-prev").prop("disabled", i === 0);
		this.products_section.find(".ws-single-next").prop("disabled", i + per_page >= n);

		// Counter: one per page → "1 / 120"; several per page → each card
		// shows its own position ("3 / 120"), so identical ranges are not
		// repeated on every card.
		if (per_page > 1) {
			$wrap.find(".ws-single-counter").each((idx, el) => {
				$(el).text(`${ i + idx + 1 } / ${ n }`);
			});
		} else {
			this.products_section.find(".ws-single-counter").text(`${ i + 1 } / ${ n }`);
		}
	}

	// ── Per-page selector ─────────────────────────────────────
	_render_perpage_select() {
		const $select = this.products_section.find(".ws-single-perpage");
		if (!$select.length) return;

		const per_page = this._per_page || this._get_per_page();
		// Options: standard set plus the effective value (in case the
		// admin setting picked something outside the standard set).
		const options = [...new Set([...this._per_page_options(), per_page])]
			.sort((a, b) => a - b);
		$select.html(options.map(n =>
			`<option value="${n}"${n === per_page ? " selected" : ""}>${n}</option>`
		).join(""));
	}

	_get_card_html(item, title) {
		// Image Only mood image (PIT-TASK-2027-000218): image only, no body,
		// no link.
		if (item.custom_image_only) {
			return `
				<div class="ws-single-card ws-single-image-only${ item.custom_fill_image ? ' ws-image-fill-card' : '' }">
					${ this._get_image_html(item, title) }
				</div>
			`;
		}
		return `
			<div class="ws-single-card">
				${ this._get_image_html(item, title) }
				${ this._get_body_html(item, title) }
			</div>
		`;
	}

	_get_image_html(item, title) {
		const ratio_class = webshop.get_image_ratio_class ? webshop.get_image_ratio_class(this.settings) : "";
		const badge_html = webshop.get_new_badge_html ? webshop.get_new_badge_html(item) : "";
		const discount_badge_html = webshop.get_discount_badge_html ? webshop.get_discount_badge_html(item) : "";
		const img = item.website_image
			? `<img itemprop="image" class="ws-single-img${ item.custom_image_only ? ' ws-mood-image' : '' }"
				src="${ item.website_image }" alt="${ title }" loading="eager"${
					item.custom_image_only
						? ` role="button" tabindex="0" aria-label="${ __("Enlarge image") }"`
						: ""
				}>`
			: `<div class="ws-single-no-image">${ frappe.get_abbr(title) }</div>`;

		const inner = `
				${ badge_html }
				${ discount_badge_html }
				${ img }
			`;

		// Detail page toggle (PIT-TASK-2027-000166): no link when disabled
		// Image Only mood images (PIT-TASK-2027-000218): never linked.
		const item_link = item.custom_image_only
			? null
			: (webshop.get_item_link ? webshop.get_item_link(item, this.settings) : `/${ item.route || '#' }`);
		if (!item_link) {
			return `<div class="ws-single-img-link${ ratio_class }">${ inner }</div>`;
		}

		return `
			<a href="${ item_link }" class="ws-single-img-link${ ratio_class }">
				${ inner }
			</a>
		`;
	}

	_get_body_html(item, title) {
		const settings = this.settings || {};

		// Price + bulk prices move into the bottom row (adj3:
		// cart left, prices right, same horizontal level — compact).
		// Sold Out (PIT-TASK-2027-000358 adjustment 1): a sold-out item
		// renders NO price section — neither Einzelpreis/MRP nor the
		// Staffelpreis table.
		let price_html = '';
		if (item.formatted_price && !item.custom_sold_out) {
			price_html = `
				<div class="ws-single-price">
					${ item.formatted_mrp
						? `<span class="ws-single-mrp">${ item.formatted_mrp }</span>` : '' }
					<span class="ws-single-rate">${ item.formatted_price }</span>
					${ item.discount_percent
						? `<span class="ws-single-discount">-${ item.discount_percent }%</span>` : '' }
				</div>`;
		}

		const bulk_html = (!item.custom_sold_out && item.bulk_prices_html)
			? `<div class="bulk-prices-wrapper ws-single-bulk">${ item.bulk_prices_html }</div>` : '';

		const price_section = (price_html || bulk_html)
			? `<div class="ws-bottom-prices">${ price_html }${ bulk_html }</div>` : '';

		// Stock
		const stock_html = this._get_stock_html(item, settings);

		// Cart action
		const cart_html = this._get_cart_html(item, settings);

		// Wishlist
		const wish_html = (!item.has_variants && settings.enable_wishlist)
			? this._get_wishlist_html(item) : '';

		// Item group
		const group_html = settings.custom_show_item_group && (item.item_group_display || item.item_group)
			? `<div class="ws-single-group">${ item.item_group_display || item.item_group }</div>` : '';

		// Detail page toggle (PIT-TASK-2027-000166): no links when disabled
		const item_link = webshop.get_item_link ? webshop.get_item_link(item, settings) : `/${ item.route || '#' }`;
		// Item's custom_additional_1 under the item name (PIT-TASK-2027-000308)
		const additional_1_html = webshop.get_custom_additional_1_html
			? webshop.get_custom_additional_1_html(item, settings) : "";
		const title_inner = `
					${ webshop.get_item_code_row_html ? webshop.get_item_code_row_html(item, settings) : '' }
					<h2 class="ws-single-title">${ title }</h2>
					${ additional_1_html }
				`;
		const title_html = item_link
			? `<a href="${ item_link }" class="ws-single-title-link">${ title_inner }</a>`
			: `<div class="ws-single-title-link">${ title_inner }</div>`;

		return `
			<div class="ws-single-body">
				<div class="ws-single-header">
					<div style="flex:1;min-width:0;">
						${ group_html }
						${ title_html }
					</div>
					${ wish_html }
				</div>

				${ stock_html }
				${ this._get_bottom_row_html(item, cart_html, price_section) }

				<div class="ws-single-meta">
					<span class="ws-single-counter"></span>
					${ item_link
						? `<a href="${ item_link }" class="ws-single-detail-link">
							${ __("View Details") } →
						</a>` : '' }
				</div>
			</div>
		`;
	}

	// Bottom row (adj3): cart controls (left) + price section (right)
	_get_bottom_row_html(item, cart_html, price_section) {
		if (!cart_html && !price_section) return '';
		return `<div class="ws-bottom-row">${ cart_html }${ price_section || '' }</div>`;
	}

	_get_stock_html(item, settings) {
		// Sold Out toggle (PIT-TASK-2027-000358): an explicitly flagged
		// item is not for sale — the availability line says so and wins
		// over every stock/dispatcher hint below (the prices stay).
		if (item.custom_sold_out)
			return `<span class="out-of-stock ws-sold-out mt-2">
				${ __("Sold Out") }</span>`;
		// "Sofort lieferbar" (PIT-TASK-2027-000280) on in-stock items —
		// mirrors the detail page's dispatcher line, independent of
		// show_stock_availability.
		if (!item.has_variants && item.is_stock && item.in_stock && !item.delivery_on_request)
			return `<span class="in-stock in-green has-stock mt-2">
				${ __("Currently available") }</span>`;
		if (!settings.show_stock_availability || item.has_variants) return '';
		if (item.on_backorder)
			return `<span class="out-of-stock mt-2" style="color:var(--primary-color)">
				${ __("Available on backorder") }</span>`;
		if (!item.in_stock)
			return `<span class="out-of-stock mt-2">${ __("Out of Stock") }</span>`;
		return '';
	}

	_get_cart_html(item, settings) {
		if (item.has_variants) {
			// Variants can only be chosen on the detail page — without it,
			// the button has no target (PIT-TASK-2027-000166).
			if (webshop.detail_page_enabled && !webshop.detail_page_enabled(settings)) {
				return '';
			}
			return `
				<div class="mt-3">
					<a href="/${ item.route || '#' }">
						<div class="btn btn-sm btn-explore-variants">${ __("Ansehen") }</div>
					</a>
				</div>`;
		}
		// Card cart controls (PIT-TASK-2027-000379): when the Webshop Settings
		// toggle is on, the single-view overview card renders no qty spinner
		// and no add-to-cart button at all (row omitted, not hidden via CSS).
		if (webshop.card_add_to_cart_hidden && webshop.card_add_to_cart_hidden(settings)) {
			return '';
		}
		if (!settings.enabled) return '';

		// Sold Out toggle (PIT-TASK-2027-000358): the item stays published
		// with its prices, but the cart controls are rendered disabled —
		// shopping_cart.js skips clicks on .ws-sold-out-btn and
		// cart.update_cart() rejects the item server-side.
		const sold_out = !!item.custom_sold_out;
		if (!sold_out && !settings.allow_items_not_in_stock && !item.in_stock) return '';

		return `
			<div class="cart-action-row mt-3">
				<div class="item-qty-area">
					<div class="input-group number-spinner">
						<span class="input-group-prepend d-sm-inline-block">
							<button class="item-qty-btn dwn-qty" data-dir="dwn"${ sold_out ? ' disabled' : '' }>&ndash;</button>
						</span>
						<input class="form-control text-center item-qty"
							value="${ item.min_qty || 1 }"
							min="${ item.min_qty || 1 }"
							step="${ item.lot_size || 1 }"
							data-item-code="${ item.item_code }"
							data-min-qty="${ item.min_qty || 1 }"
							data-lot-size="${ item.lot_size || 1 }"${ sold_out ? ' disabled' : '' }>
						<span class="input-group-append d-sm-inline-block">
							<button class="item-qty-btn up-qty" data-dir="up"${ sold_out ? ' disabled' : '' }>+</button>
						</span>
					</div>
				</div>
				<div id="${ item.name }" class="btn btn-sm btn-primary btn-add-to-cart-list${ sold_out ? ' disabled ws-sold-out-btn' : '' }"
					data-item-code="${ item.item_code }"${ sold_out ? ' data-sold-out="1" aria-disabled="true"' : '' }
					title="${ sold_out ? __("Sold Out") : (settings.enable_checkout ? __("Add to Cart") : __("Add to Quote")) }">
					<svg class="icon icon-md" viewBox="0 0 24 24" fill="none"><path d="M3 6h17" stroke="currentColor" stroke-miterlimit="10"></path><path d="M20 19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6l3.4-3h10.2L20 6v13z" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"></path><path d="M15.582 10c0 2.29-1.8 4.09-4.091 4.09A4.051 4.051 0 0 1 7.4 10" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path><circle cx="15.6" cy="10" r=".5" stroke="currentColor"></circle><circle cx="7.3" cy="10" r=".5" stroke="currentColor"></circle></svg>
					${ item.in_cart ? `<span class="ws-cart-count-badge" data-item-code="${ item.item_code }">${ item.in_cart_qty }</span>` : '' }
				</div>
			</div>`;
	}

	_get_wishlist_html(item) {
		const cls = item.wished ? "wished" : "not-wished";
		return `
			<div class="like-action ws-single-wish ${ cls }" data-item-code="${ item.item_code }">
				<svg class="icon icon-md"><use href="#icon-heart"></use></svg>
			</div>`;
	}

	// ── Navigation ───────────────────────────────────────────────

	_bind_nav() {
		const me = this;
		this.products_section.on("click", ".ws-single-prev", () => {
			if (me.current_index > 0)
				me._fetch(Math.max(0, me.current_index - (me._per_page || 1)));
		});
		this.products_section.on("click", ".ws-single-next", () => {
			const step = me._per_page || 1;
			if (me.current_index + step < me.total_count)
				me._fetch(me.current_index + step);
		});
		this.products_section.on("change", ".ws-single-perpage", (e) => {
			const val = cint($(e.target).val()) || 1;
			localStorage.setItem("ws_single_per_page", val);
			// Keep roughly the same position: realign current index to
			// the start of the page that contains it under the new N.
			const new_start = Math.floor(me.current_index / val) * val;
			me._fetch(new_start);
		});
	}

	// ── Qty events ───────────────────────────────────────────────

	_bind_qty_events() {
		this.products_section.on("input", ".item-qty-area .item-qty", (e) => {
			e.target.value = e.target.value.replace(/[^0-9]/g, "");
		});
		this.products_section.on("click", ".item-qty-area .up-qty", (e) => {
			const $q = $(e.target).closest(".item-qty-area").find(".item-qty");
			const step = parseInt($q.data("lot-size")) || 1;
			$q.val((parseInt($q.val()) || 0) + step);
		});
		this.products_section.on("click", ".item-qty-area .dwn-qty", (e) => {
			const $q = $(e.target).closest(".item-qty-area").find(".item-qty");
			const step = parseInt($q.data("lot-size")) || 1;
			const minQty = parseInt($q.data("min-qty")) || 1;
			$q.val(Math.max((parseInt($q.val()) || 0) - step, minQty));
		});
	}
};
